import React, { useEffect } from "react";
import submit from "submit-form";
import moment from "moment";
import { DATE_FORMATS } from "../../../../../constants";
import ExerciseEditor from "./ExerciseEditor/ExerciseEditor";
import Footer from "./ExerciseEditor/Footer";
import Sortable from "sortablejs";
import SvgIcon from "../../../../../parts/SvgIcon";
import { PasteExerciseComponent } from "../../../../../parts/PasteExerciseComponent";
import { CopyExerciseComponent } from "../../../../../parts/CopyExerciseComponent";

const { DATE_DAY_FORMAT } = DATE_FORMATS;

const DayEditor = ({
  date,
  editDayMode,
  toggleEditDayMode,
  showAddExercise,
  exercises,
  resetExercises,
  handleRemoveMultiple,
  saveEditedExercise,
  setState,
  dayString,
  athleteId,
  tempExercises,
  supersets,
  handleSelectChange,
  selectedExercise,
  showOptions,
  activeUser,
  athlete,
  getTempExercise,
  handleInput,
  addTempSet,
  repMaximums,
  removeSet,
  handleSuperset,
  handleEditTemporalInput,
  withWeight,
  fromMetric,
  intensityMeasure,
  isRiR,
  opacity,
  activeUserToRequest,
  scheduledSessions,
  flash,
  removeTempExercise,
  query,
}) => {
  useEffect(() => {
    const formattedDate = date.format(DATE_DAY_FORMAT);
    const element = document.getElementById(formattedDate);
    let sortable = null;
    const dragImage = document.createElement("img");
    dragImage.src =
      "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
    if (element) {
      sortable = Sortable.create(element, {
        group: "month-exercises",
        animation: 350,
        handle: ".grabber",
        draggable: ".exercisex",
        setData(dataTransfer) {
          dataTransfer.setDragImage(dragImage, 0, 0);
        },
        onEnd: (e) => {
          const { item, from, to, oldIndex, newIndex } = e;
          const sameList = from === to;

          if (oldIndex === newIndex && sameList) return;

          const fromItems = from.querySelectorAll(":scope > .exercisex");
          const toItems = to.querySelectorAll(":scope > .exercisex");
          const toDate = to.id;
          let withinSuperset = false;
          const fromExercises = [];
          const toExercises = [];
          let exercises = scheduledSessions[formattedDate] || [];
          if (!sameList) {
            exercises = [...exercises, ...(scheduledSessions[toDate] || [])];
          }

          for (let i = 0; i < fromItems.length; i++) {
            const element = fromItems[i];
            const nextElement = fromItems[i + 1];
            const exercise = exercises.find((exercise) => {
              return exercise._id == element.id;
            });

            if (
              exercise?.nextSuperset &&
              exercise.nextSuperset != nextElement?.id
            ) {
              withinSuperset = true;
            }
            fromExercises.push(element.id);
          }
          if (!sameList) {
            for (let i = 0; i < toItems.length; i++) {
              const element = toItems[i];
              const nextElement = toItems[i + 1];
              const exercise = exercises.find((exercise) => {
                return exercise._id == element.id;
              });

              if (
                exercise?.nextSuperset &&
                exercise.nextSuperset != nextElement?.id
              ) {
                withinSuperset = true;
              }
              toExercises.push(element.id);
            }
            from.insertBefore(item, fromItems[oldIndex]);
          } else {
            from.insertBefore(
              item,
              fromItems[oldIndex + (oldIndex > newIndex)]
            );
          }

          let body = {
            exercises: fromExercises,
          };

          if (!sameList && toExercises.length > 0) {
            body = {
              ...body,
              toExercises,
              toDate: moment(toDate, DATE_DAY_FORMAT).startOf("day").toJSON(),
            };
          }

          if (withinSuperset) {
            flash("error", "Cannot reorder within a superset");
            submit("/actions/refresh");
          } else {
            removeTempExercise(item.id, sameList, fromExercises);
            submit("/actions/exercise/workout-reorder", {
              method: "POST",
              body,
            });
          }
        },
      });
    }

    return () => {
      if (sortable) {
        sortable.destroy();
      }
      dragImage.remove();
    };
  }, [scheduledSessions]);

  const handleClick = (evt) => {
    if (evt.target.alt === "dismiss") {
      evt.preventDefault();
      return;
    }
    if (
      evt.target.href &&
      evt.target.href.indexOf("calendar-add-exercise") > 0
    ) {
      return;
    }
    if (exercises.length) {
      // Avoiding activate the edit mode if click on the options meatball.
      toggleEditDayMode();
    }
  };
  return (
    <div className="day-editor" style={{ opacity: opacity ? 0.3 : 1 }}>
      <header>
        <p className="s1">{date.format("D")}</p>
        <div className="flex items-start gap-2">
          {exercises && exercises.length > 0 && (
            <CopyExerciseComponent
              classNames="!p-0 bg-[--secondary-background-color] h-7 w-7"
              exercises={exercises}
              athlete={athlete}
            />
          )}
          {exercises && exercises.length === 0 && (
            <PasteExerciseComponent
              athlete={athlete}
              date={date.format(DATE_DAY_FORMAT)}
              classNames="p-0 bg-[--secondary-background-color] h-7 w-7"
              withFullInfo={false}
              isProgram={false}
              query={query?.exercisesInClipboard}
            />
          )}
        </div>
      </header>
      <section className={editDayMode ? "height-tight" : ""}>
        <div
          className={`exercises${editDayMode ? " editing" : ""}`}
          id={date.format(DATE_DAY_FORMAT)}
          onClick={handleClick}
        >
          {exercises && exercises.length ? (
            exercises.map((exercise, idx) => (
              <ExerciseEditor
                key={idx}
                keyId={idx}
                {...{
                  exercise,
                  exercises,
                  activeUser,
                  athlete,
                  editDayMode,
                  dayString,
                  athleteId,
                  tempExercises,
                  supersets,
                  handleSelectChange,
                  selectedExercise,
                  showOptions,
                  getTempExercise,
                  handleInput,
                  addTempSet,
                  repMaximums,
                  removeSet,
                  handleSuperset,
                  handleEditTemporalInput,
                  withWeight,
                  fromMetric,
                  intensityMeasure,
                  isRiR,
                  activeUserToRequest,
                  date,
                  saveEditedExercise,
                }}
              />
            ))
          ) : (
            <div
              className="no-exercises-container"
              onMouseEnter={() => setState({ showAddExercise: true })}
              onMouseLeave={() => setState({ showAddExercise: false })}
            >
              <div
                className="add-paste-exercise"
                style={showAddExercise ? { opacity: 1 } : { opacity: 0 }}
              >
                <a
                  href={`/actions/refresh?modal[type]=calendar-add-exercise&day=${date.format(
                    DATE_DAY_FORMAT
                  )}`}
                  aria-disabled={!showAddExercise}
                >
                  <SvgIcon
                    src="/img/new-icons/add-square-outline.svg"
                    className="h-11 w-11"
                  />
                  <p className="p4 text-center">Add Exercise</p>
                </a>
                <PasteExerciseComponent
                  athlete={athlete}
                  date={date.format(DATE_DAY_FORMAT)}
                  withFullInfo={true}
                  showAddExercise={showAddExercise}
                  isProgram={false}
                  query={query?.exercisesInClipboard}
                />
              </div>
            </div>
          )}
        </div>
        <Footer
          date={date}
          editDayMode={editDayMode}
          handleRemoveMultiple={handleRemoveMultiple}
          resetExercises={resetExercises}
          saveEditedExercise={saveEditedExercise}
          toggleEditDayMode={toggleEditDayMode}
          hasExercise={exercises && exercises.length}
          athleteId={athleteId}
          exercisesInClipboard={query?.exercisesInClipboard}
          athlete={athlete}
          exercises={exercises}
        />
      </section>
    </div>
  );
};

export default DayEditor;
